import pmDecisionService from '@/services/pmDecisionService';

export default {
  namespaced: true,

  state: {
    decisions: [],
    total: 0,
    projectId: null,
    loading: false,
    error: null,
  },

  getters: {
    decisions: (state) => state.decisions,
    total: (state) => state.total,
    projectId: (state) => state.projectId,
    loading: (state) => state.loading,
    error: (state) => state.error,
    isEmpty: (state) => !state.loading && !state.error && state.decisions.length === 0,
    hasProjectContext: (state) => Boolean(state.projectId),
    openDecisions: (state) => state.decisions.filter((item) => item.status === 'open'),
    pendingDecisions: (state) => state.decisions.filter(
      (item) => item.status === 'open' || item.status === 'prepared' || item.status === 'escalated',
    ),
  },

  mutations: {
    SET_PROJECT_ID(state, projectId) {
      state.projectId = projectId;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_DECISIONS(state, { items, total }) {
      state.decisions = items || [];
      state.total = typeof total === 'number' ? total : state.decisions.length;
    },
    ADD_DECISION(state, decision) {
      state.decisions.unshift(decision);
      state.total += 1;
    },
    UPDATE_DECISION(state, updated) {
      const index = state.decisions.findIndex((d) => d.id === updated.id);
      if (index !== -1) {
        state.decisions.splice(index, 1, updated);
      }
    },
    REMOVE_DECISION(state, id) {
      state.decisions = state.decisions.filter((d) => d.id !== id);
      state.total = Math.max(0, state.total - 1);
    },
    RESET(state) {
      state.decisions = [];
      state.total = 0;
      state.projectId = null;
      state.loading = false;
      state.error = null;
    },
  },

  actions: {
    setProjectContext({ commit, dispatch }, projectId) {
      commit('SET_PROJECT_ID', projectId);
      if (projectId) {
        return dispatch('fetchDecisions');
      }
      commit('SET_DECISIONS', { items: [], total: 0 });
      return Promise.resolve();
    },

    async fetchDecisions({ commit, state }) {
      if (!state.projectId) {
        commit('SET_DECISIONS', { items: [], total: 0 });
        return;
      }
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const data = await pmDecisionService.listDecisions(state.projectId);
        commit('SET_DECISIONS', {
          items: data?.items || [],
          total: data?.total ?? 0,
        });
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load decisions';
        commit('SET_ERROR', message);
        commit('SET_DECISIONS', { items: [], total: 0 });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createDecision({ commit, state }, payload) {
      commit('SET_ERROR', null);
      try {
        const decision = await pmDecisionService.createDecision(state.projectId, payload);
        commit('ADD_DECISION', decision);
        return decision;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to create decision';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async updateDecision({ commit, state }, { id, payload }) {
      commit('SET_ERROR', null);
      try {
        const decision = await pmDecisionService.updateDecision(state.projectId, id, payload);
        commit('UPDATE_DECISION', decision);
        return decision;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to update decision';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async updateDecisionStatus({ commit, state }, { id, status }) {
      commit('SET_ERROR', null);
      try {
        const decision = await pmDecisionService.updateStatus(state.projectId, id, status);
        commit('UPDATE_DECISION', decision);
        return decision;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to update decision status';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async escalateDecision({ commit, state }, id) {
      commit('SET_ERROR', null);
      try {
        const decision = await pmDecisionService.escalateDecision(state.projectId, id);
        commit('UPDATE_DECISION', decision);
        return decision;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to escalate decision';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async deleteDecision({ commit, state }, id) {
      commit('SET_ERROR', null);
      try {
        await pmDecisionService.deleteDecision(state.projectId, id);
        commit('REMOVE_DECISION', id);
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to delete decision';
        commit('SET_ERROR', message);
        throw error;
      }
    },
  },
};
